import React, { SyntheticEvent, useMemo } from 'react';
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from '@splidejs/react-splide';
import classNames from 'classnames';

import ColorBubble, {
  ClassesType as BubbleClassesType,
} from 'components/Common/ColorBubble/ColorBubble';
import HideArrowsExtension from 'components/Common/Slide/Extensions/HideArrowsExtension';
import { getUri } from 'services/generic';

import styles from './SlideColorBubbles.module.scss';

type PropsType = {
  bubbles: SlideColorBubbleType[];
  classes?: ClassesType;
  extensions?: Record<string, unknown>;
  onBubbleClick?: (bubble: SlideColorBubbleType, e: SyntheticEvent) => void;
  onBubbleOver?: (bubble: SlideColorBubbleType, e: SyntheticEvent) => void;
  onBubbleLeave?: (bubble: SlideColorBubbleType, e: SyntheticEvent) => void;
  options?: Options;
  selectedColor?: string;
};

type ClassesType = {
  arrow?: string;
  container?: string;
  nextArrow?: string;
  prevArrow?: string;
  bubbles?: BubbleClassesType;
};

export type SlideColorBubbleType = {
  color: string;
  image: string;
  url: string;
};

const DEFAULT_PER_PAGE = 3;

export default function SlideColorBubbles({
  bubbles,
  classes,
  extensions,
  onBubbleClick,
  onBubbleOver,
  onBubbleLeave,
  options,
  selectedColor,
}: PropsType): JSX.Element {
  const { perPage = DEFAULT_PER_PAGE } = { ...options };

  const slideOptions: Options = useMemo(() => {
    return {
      ...{
        pagination: false,
        updateOnMove: false,
        mediaQuery: 'min',
        perPage: perPage,
        perMove: 1,
        classes: {
          arrow: classNames('splide__arrow', styles.arrow, classes?.arrow, {}),
          next: classNames(
            'splide__arrow--next',
            styles.nextArrow,
            classes?.nextArrow
          ),
          prev: classNames(
            'splide__arrow--prev',
            styles.prevArrow,
            classes?.prevArrow
          ),
        },
      },
      ...options,
    };
  }, []);

  function renderBubble(bubble: SlideColorBubbleType) {
    const { color, image, url } = bubble;
    return (
      <ColorBubble
        classes={{
          container: classes?.bubbles?.container,
          image: classNames(styles.colorImage, classes?.bubbles?.image),
        }}
        color={color}
        image={image}
        selected={selectedColor === color}
        url={getUri(url)}
        onClick={e => onBubbleClick?.(bubble, e)}
      />
    );
  }

  return (
    <Splide
      options={slideOptions}
      className={classNames(styles.carousel, classes?.container)}
      hasTrack={false}
      extensions={{
        hideArrows: HideArrowsExtension,
        ...extensions,
      }}
    >
      <SplideTrack
        className={classNames(styles.track, {
          [styles.centerItems]: perPage >= bubbles.length,
        })}
      >
        {bubbles.map(bubble => {
          const { color } = bubble;
          return (
            <SplideSlide
              key={color}
              className={styles.slide}
              onMouseOver={e => onBubbleOver?.(bubble, e)}
              onMouseLeave={e => onBubbleLeave?.(bubble, e)}
            >
              {renderBubble(bubble)}
            </SplideSlide>
          );
        })}
      </SplideTrack>
    </Splide>
  );
}
